import AlertBox from "@components/AlertBox";
import DashboardItem from "@components/DashboardItem";
import ClaimAll from "@components/Modals/ClaimAll";
import CompoundAll from "@components/Modals/CompoundAll";
import CreateNode from "@components/Modals/CreateNode";
import NodeList from "@components/NodeList";
import config from "@config";
import defaultConfig from "@config";
import useAccount from "@hooks/useAccount";
import useAllNodes from "@hooks/useAllNodes";
import useAllowance from "@hooks/useAllowance";
import useApprove from "@hooks/useApprove";
import useAvailableRewards from "@hooks/useAvailableRewards";
import useContract from "@hooks/useContract";
import useMediaQuery from "@hooks/useMediaQuery";
import useMigrateContract from "@hooks/useMigrateContract";
import useNodesTotalAmount from "@hooks/useNodesTotalAmount";
import useStratosphere from "@hooks/useStratosphere";
import useToast from "@hooks/useToast";
import { useTotalValueLocked } from "@hooks/useTotalValueLocked";
import { useVPNDPrice } from "@hooks/useVPNDPrice";
import type { MyLocale } from "@i18n";
import LoadingButton from "@mui/lab/LoadingButton";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import { useAppSelector } from "@store/hooks";
import {
	contractMigrationRequired,
	contractNodeManagerSelector,
} from "@store/selectors/contract";
import formatRewards from "@utils/formatRewards";
import { useI18n } from "next-rosetta";
import { useEffect, useState } from "react";
import { maxUint256 } from "viem";
import type { Address } from "viem";
import ExternalLink from "./ExternalLink";

export default function ExistingUser({ enableNodeCreation }) {
	const vpndPrice = useVPNDPrice();
	const [isCreateNodeOpen, setIsCreateNodeOpen] = useState(false);
	const [isCompoundAllOpen, setIsCompoundAllOpen] = useState(false);
	const [isClaimAllOpen, setIsClaimAllOpen] = useState(false);
	const { account } = useAccount();
	const { nodeController05Contract, tokenContract } = useContract();
	const availableRewards = useAvailableRewards(account);
	const { nodesTotalAmount } = useNodesTotalAmount(account);
	const tvl = useTotalValueLocked();
	const spender = config.web3.vaporControllerAddress as Address;
	const approveAmt = maxUint256;
	const { isLoading: isLoadingApprove, send: approve } = useApprove(
		tokenContract?.address as Address,
		spender,
		approveAmt,
	);

	const allowance = useAllowance(account ?? undefined, spender);
	const nodes = useAllNodes(account);
	const { isMobile } = useMediaQuery();
	const i18n = useI18n<MyLocale>();
	const [selected, setSelected] = useState<readonly number[]>([]);
	const isMigrationRequired = useAppSelector(contractMigrationRequired);
	const nodeManagerVersion = useAppSelector(contractNodeManagerSelector);
	const {
		isLoading: isMigrationLoading,
		send: migrateNodes,
		state: migrateState,
	} = useMigrateContract(nodeController05Contract?.address);
	const { contractFunctionToast } = useToast();

	const handleMigrate = () => migrateNodes();

	const handleApprove = () => approve();

	const handleCreateNode = () => setIsCreateNodeOpen(true);

	const { status } = useStratosphere(account);

	const handleSetOpenClaimAll = (value: boolean) => {
		setIsClaimAllOpen(value);
		setSelected([]);
	};

	const handleClick = (event: React.MouseEvent<unknown>, name: number) => {
		const selectedIndex = selected.indexOf(name);
		let newSelected: readonly number[] = [];

		switch (selectedIndex) {
			case -1: {
				newSelected = [...newSelected, ...selected, name];

				break;
			}
			case 0: {
				newSelected = [...newSelected, ...selected.slice(1)];

				break;
			}
			case selected.length - 1: {
				newSelected = [...newSelected, ...selected.slice(0, -1)];

				break;
			}
			default: {
				if (selectedIndex > 0) {
					newSelected = [
						...newSelected,
						...selected.slice(0, selectedIndex),
						...selected.slice(selectedIndex + 1),
					];
				}
			}
		}

		setSelected(newSelected);
	};
	const isSelected = (name: number) => selected.includes(name);

	useEffect(() => {
		if (migrateState) {
			contractFunctionToast("Migrate", migrateState);
		}
	}, [migrateState, contractFunctionToast]);

	const freezedRewards =
		nodes
			?.map(({ rewards }) => +rewards?.toString() / 1e18)
			?.reduce((a, b) => +a + +b, 0)
			?.toFixed(3) ?? "0";

	return (
		<>
			<Grid container spacing={4}>
				<Grid item xs={12} sm={12} md={12} lg={4}>
					<DashboardItem
						isBgAnimated={Boolean(Number(availableRewards))}
						title={i18n.t("nodes.dashboardRewardsTitle")}
						content={
							isMigrationRequired
								? freezedRewards?.toString()
								: formatRewards(BigInt(availableRewards ?? 0))
						}
						variant="outlined"
					/>
				</Grid>

				<Grid item xs={12} sm={6} md={6} lg={4}>
					<DashboardItem
						title={i18n.t("nodes.dashboardTotalNodesAmountTitle")}
						content={formatRewards(nodesTotalAmount)}
						variant="outlined"
					/>
				</Grid>

				{isMigrationRequired ? (
					<Grid item xs={12} sm={6} md={6} lg={4}>
						<DashboardItem
							title={i18n.t("nodes.dashboardRewardsTitleUsd")}
							content={
								isMigrationRequired
									? (+freezedRewards * +vpndPrice)?.toFixed(3)?.toString()
									: `$${
											((+availableRewards / 1e18) * +vpndPrice).toFixed(
												2,
											) as string
										}`
							}
							variant="outlined"
						/>
					</Grid>
				) : (
					<Grid item xs={12} sm={6} md={6} lg={4}>
						<DashboardItem
							title={i18n.t("nodes.dashboardTVLTitle")}
							content={formatRewards(BigInt(tvl ?? 0))}
							variant="outlined"
						/>
					</Grid>
				)}
				{isMigrationRequired && nodeManagerVersion === 4 && (
					<Grid item xs={12}>
						<AlertBox
							action={
								<LoadingButton
									color="inherit"
									size="small"
									variant="outlined"
									onClick={handleMigrate}
									disabled={isMigrationLoading}
									loading={isMigrationLoading}
								>
									{i18n.t("nodes.migrateCTA")}
								</LoadingButton>
							}
						>
							{i18n.t("nodes.migrateAlertContent")}
						</AlertBox>
					</Grid>
				)}
				{allowance === "0" ? (
					<Grid item xs={12}>
						<AlertBox
							action={
								<LoadingButton
									size="small"
									variant="outlined"
									color="warning"
									onClick={handleApprove}
									disabled={isLoadingApprove}
									loading={isLoadingApprove}
								>
									{i18n.t("nodes.approveCTA")}
								</LoadingButton>
							}
						>
							{i18n.t("nodes.approveAlertContent")}
						</AlertBox>
					</Grid>
				) : null}

				{!isMigrationRequired && (
					<Grid item xs={12}>
						<AlertBox>{i18n.t("nodes.gasLimitWarning")}</AlertBox>
					</Grid>
				)}
				{isMigrationRequired ? (
					<Grid item xs={12}>
						<AlertBox severity="info">
							<strong>{i18n.t("nodes.alertNewTaxSystem1")}</strong>
							{i18n.t("nodes.alertNewTaxSystem2")}
							<ExternalLink
								warning
								href={defaultConfig.documentation.nodesTransformation}
							>
								{i18n.t("nodes.alertNewTaxSystem3")}{" "}
							</ExternalLink>
							{i18n.t("nodes.alertNewTaxSystem4")}
							<ExternalLink warning href={defaultConfig.social.twitter}>
								Twitter
							</ExternalLink>
							{i18n.t("nodes.alertNewTaxSystem5")}
							<ExternalLink warning href={defaultConfig.social.discord}>
								Discord
							</ExternalLink>
							{i18n.t("nodes.alertNewTaxSystem6")}
						</AlertBox>
					</Grid>
				) : null}

				<Grid item xs={12}>
					<Grid
						item
						xs={12}
						container
						spacing={2}
						justifyContent={isMobile ? "center" : "space-between"}
						flexDirection={isMobile ? "column" : "row"}
						maxWidth="calc(100vh - 32px)"
					>
						<Grid item>
							<Stack
								spacing={2}
								direction={isMobile ? "column" : "row"}
								justifyContent="left"
								flexWrap="wrap"
							>
								<Tooltip
									title={i18n.t("nodes.tooltipRewardsReinvested")}
									placement="top"
								>
									<span>
										<Button
											variant="contained"
											color="primary"
											onClick={() => setIsCompoundAllOpen(true)}
											sx={{ width: isMobile ? "100%" : undefined }}
											disabled={
												!selected?.length ||
												allowance === "0" ||
												isMigrationRequired ||
												+availableRewards === 0
											}
										>
											{i18n.t("nodes.compoundAllNodesCTA")}
										</Button>
									</span>
								</Tooltip>
								<Tooltip
									title={i18n.t("nodes.tooltipCollectRewards")}
									placement="top"
								>
									<span>
										<Button
											variant="contained"
											onClick={() => setIsClaimAllOpen(true)}
											sx={{ width: isMobile ? "100%" : undefined }}
											disabled={
												!selected?.length ||
												allowance === "0" ||
												isMigrationRequired ||
												+availableRewards === 0
											}
										>
											{i18n.t("nodes.claimAllNodesCTA")}
										</Button>
									</span>
								</Tooltip>
							</Stack>
						</Grid>

						<Grid item>
							<Stack
								spacing={2}
								direction={isMobile ? "column" : "row"}
								justifyContent="right"
								flexWrap="wrap"
							>
								<Button
									variant="contained"
									onClick={enableNodeCreation ? handleCreateNode : null}
									sx={{ width: isMobile ? "100%" : undefined }}
									disabled={
										!enableNodeCreation ||
										(isMigrationRequired
											? true
											: status === "member"
												? false
												: nodes?.length >= 5)
									}
								>
									{i18n.t("nodes.createNodesCTA")}
								</Button>
							</Stack>
						</Grid>
					</Grid>
				</Grid>

				<Grid item xs={12}>
					<NodeList
						nodes={nodes}
						onRowClick={handleClick}
						checkRowSelection={isSelected}
					/>
				</Grid>
			</Grid>

			{/* Modals */}

			{!isMigrationRequired && (
				<CreateNode open={isCreateNodeOpen} setOpen={setIsCreateNodeOpen} />
			)}
			{!isMigrationRequired && selected.length > 0 ? (
				<ClaimAll
					open={isClaimAllOpen}
					setOpen={handleSetOpenClaimAll}
					nodeIDs={selected}
				/>
			) : null}
			{!isMigrationRequired && (
				<CompoundAll
					open={isCompoundAllOpen}
					setOpen={setIsCompoundAllOpen}
					nodeIDs={selected?.map(BigInt)}
				/>
			)}
		</>
	);
}
